import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, InputNumber, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import type {
  FeatureAccessDto,
  FeatureDefinitionDto,
  FeatureKeyDto,
  FeaturePermissionMatrixDto,
  FeaturePermissionMatrixResponseDto,
  FeatureRuleDto,
  FeatureTableRowDto,
  MemberPlanTierDto,
} from '@wellverse/shared-ts';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';

const TIERS: { key: MemberPlanTierDto; label: string }[] = [
  { key: 'FREE', label: 'Free user' },
  { key: 'EFX', label: 'EFX member' },
  { key: 'PREMIUM', label: 'Premium user' },
];

const ACCESS_LABELS: Record<FeatureAccessDto, string> = {
  ALLOW: 'Allowed',
  DENY: 'Not allowed',
  LIMIT: 'Daily limit',
  READ_ONLY: 'Read only',
};

/** One cell: the rule for one feature and one member type. */
const RuleCell: React.FC<{
  definition: FeatureDefinitionDto;
  rule: FeatureRuleDto;
  onChange: (rule: FeatureRuleDto) => void;
}> = ({ definition, rule, onChange }) => (
  <Space size={8} wrap>
    <Select<FeatureAccessDto>
      value={rule.access}
      style={{ width: 130 }}
      status={rule.access === 'ALLOW' ? undefined : 'warning'}
      options={definition.allowedAccess.map((access) => ({ value: access, label: ACCESS_LABELS[access] }))}
      onChange={(access) =>
        // A limit needs a number; start from what the cell had, or 1.
        onChange(access === 'LIMIT' ? { access, dailyLimit: rule.dailyLimit ?? 1 } : { access })
      }
    />
    {rule.access === 'LIMIT' && (
      <InputNumber
        min={1}
        max={1000}
        precision={0}
        value={rule.dailyLimit}
        addonAfter="/ day"
        style={{ width: 120 }}
        onChange={(value) => onChange({ access: 'LIMIT', dailyLimit: value ?? 1 })}
      />
    )}
  </Space>
);

/**
 * What each member type may use — the product's permission table, editable.
 *
 * The API enforces whatever is saved here on the next request, so this is also
 * the switch for opening a feature up (or closing it) without a release.
 */
export const MemberPermissionsPage: React.FC = () => {
  const [features, setFeatures] = useState<FeatureDefinitionDto[]>([]);
  const [rows, setRows] = useState<FeatureTableRowDto[]>([]);
  const [saved, setSaved] = useState<FeaturePermissionMatrixDto | null>(null);
  const [draft, setDraft] = useState<FeaturePermissionMatrixDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = (response: FeaturePermissionMatrixResponseDto) => {
    setFeatures(response.features);
    setRows(response.rows);
    setSaved(response.matrix);
    setDraft(response.matrix);
  };

  useEffect(() => {
    axiosInstance
      .get<FeaturePermissionMatrixResponseDto>(`${API_BASE_URL}/admin/entitlements/matrix`)
      .then(({ data }) => apply(data))
      .catch((error) => message.error(error?.message || 'Could not load the permission table'))
      .finally(() => setLoading(false));
  }, []);

  const dirty = useMemo(() => JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);

  const setRule = (feature: FeatureKeyDto, tier: MemberPlanTierDto, rule: FeatureRuleDto) =>
    setDraft((current) =>
      current ? { ...current, [feature]: { ...current[feature], [tier]: rule } } : current,
    );

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.put<FeaturePermissionMatrixResponseDto>(
        `${API_BASE_URL}/admin/entitlements/matrix`,
        { matrix: draft },
      );
      apply(data);
      message.success('Permissions saved — they apply to the next request');
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin />;
  if (!draft) return <Alert type="error" showIcon message="The permission table could not be loaded." />;

  return (
    <Card
      title="Member Permissions"
      extra={
        <Space>
          <Button icon={<UndoOutlined />} disabled={!dirty || saving} onClick={() => setDraft(saved)}>
            Discard changes
          </Button>
          <Button type="primary" icon={<SaveOutlined />} disabled={!dirty} loading={saving} onClick={save}>
            Save
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="What each member type may use — the full product table. Rows with dropdowns are enforced and can be changed here."
        description={
          <>
            A daily limit counts in the member's own day and resets at their midnight. Restrictions only stop
            something new being made: friends and workout plans a member already has stay usable if they
            move back to Free. Rows marked "Open to everyone" have no restriction to edit, and rows marked
            "Not built yet" are features still to come.
          </>
        }
      />
      <Table<FeatureTableRowDto>
        dataSource={rows}
        rowKey={(row) => `${row.category}/${row.label}`}
        pagination={false}
        size="middle"
        scroll={{ x: 1100 }}
      >
        <Table.Column<FeatureTableRowDto>
          title="Category"
          width={130}
          // Like the sheet: the category is named once, on its first row.
          render={(_, row, index) =>
            index === 0 || rows[index - 1].category !== row.category ? row.category : null
          }
        />
        <Table.Column<FeatureTableRowDto>
          title="Feature"
          width={300}
          render={(_, row) => (
            <Space size={8} wrap>
              <Typography.Text strong={row.status === 'CONFIGURABLE'} type={row.status === 'PLANNED' ? 'secondary' : undefined}>
                {row.label}
              </Typography.Text>
              {row.status === 'OPEN' && <Tag>Open to everyone</Tag>}
              {row.status === 'PLANNED' && <Tag color="default">Not built yet</Tag>}
            </Space>
          )}
        />
        {TIERS.map((tier) => (
          <Table.Column<FeatureTableRowDto>
            key={tier.key}
            title={tier.label}
            render={(_, row) => {
              const definition = row.key ? features.find((f) => f.key === row.key) : undefined;
              if (row.key && definition) {
                return (
                  <RuleCell
                    definition={definition}
                    rule={draft[row.key][tier.key]}
                    onChange={(rule) => setRule(row.key!, tier.key, rule)}
                  />
                );
              }
              const access = row.fixed?.[tier.key];
              return (
                <Typography.Text type="secondary">
                  {access === 'ALLOW' ? '✓' : '✗'}
                </Typography.Text>
              );
            }}
          />
        ))}
      </Table>
    </Card>
  );
};
